import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';

import type { Attachment, CreditMemo } from '@stuut/api/models';
import { formatFileSize } from '@stuut/common/src/utils';
import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';
import { MAX_TOTAL_ATTACHMENT_SIZE } from '@/constants/Attachments';

export interface AttachableEntity {
  attachments?: Attachment[];
  attachmentsTotalSize: number;
  id: string;
}

interface CreditMemoAttachableResource<M extends AttachableEntity> {
  attachCreditMemo: (entity: M, creditMemoId: string) => Promise<unknown>;
  removeAttachment: (entity: M, attachment: Attachment) => Promise<unknown>;
}

interface UseCreditMemoAttachmentsOptions<M extends AttachableEntity> {
  disabled?: MaybeRefOrGetter<boolean>;
  entity: MaybeRefOrGetter<M | null | undefined>;
  partnerId: MaybeRefOrGetter<string | undefined>;
  resource: CreditMemoAttachableResource<M>;
}

export const filterByNumber = <T extends { number?: string | null }>(
  list: T[],
  term: string,
): T[] => {
  const lower = term.toLowerCase();
  return list.filter((i) => i.number?.toLowerCase()?.includes(lower));
};

export interface CreditMemoAttachments {
  allCreditMemosAttached: ComputedRef<boolean>;
  attachableCreditMemos: ComputedRef<CreditMemo[]>;
  creditMemos: Ref<CreditMemo[]>;
  creditMemoSearch: Ref<string>;
  handleAttachAllCreditMemos: () => Promise<void>;
  isCreditMemoAttached: (cm: CreditMemo) => boolean;
  isCreditMemoPending: (cm: CreditMemo) => boolean;
  toggleAttachCreditMemo: (cm: CreditMemo) => Promise<boolean>;
}

export function useCreditMemoAttachments<M extends AttachableEntity>(
  options: UseCreditMemoAttachmentsOptions<M>,
): CreditMemoAttachments {
  const creditMemos = ref<CreditMemo[]>([]);
  const creditMemoSearch = ref('');
  const fetchingCreditMemoIds = ref<string[]>([]);
  // Tracks which attachment id was created for each credit memo so the checkbox
  // state and detach lookup don't depend on a fragile filename guess.
  const creditMemoAttachmentIds = ref<Record<string, string>>({});

  let fetchToken = 0;

  const currentAttachmentIds = (): string[] =>
    toValue(options.entity)
      ?.attachments?.map((a) => a.id)
      .filter(Boolean) ?? [];

  const attachmentIdSet = computed<Set<string>>(
    () => new Set(toValue(options.entity)?.attachments?.map((a) => a.id) ?? []),
  );

  const attachmentForCreditMemo = (cm: CreditMemo): Attachment | undefined => {
    const attachmentId = creditMemoAttachmentIds.value[cm.id];
    if (!attachmentId || !attachmentIdSet.value.has(attachmentId)) return undefined;
    return toValue(options.entity)?.attachments?.find((att) => att.id === attachmentId);
  };

  const isCreditMemoAttached = (cm: CreditMemo): boolean => {
    const attachmentId = creditMemoAttachmentIds.value[cm.id];
    return !!attachmentId && attachmentIdSet.value.has(attachmentId);
  };

  const isCreditMemoPending = (cm: CreditMemo): boolean =>
    fetchingCreditMemoIds.value.includes(cm.id);

  const hasCreditMemoPdf = (cm: CreditMemo): boolean => !!cm.pdf;

  const fetchCreditMemos = async () => {
    const partnerId = toValue(options.partnerId);
    if (!partnerId) {
      creditMemos.value = [];
      return;
    }

    const token = ++fetchToken;
    try {
      const page = await stuut.creditMemos.list(
        {
          params: {
            partner: { in: [partnerId] },
            status: { in: ['open'] },
          },
        },
        true /* paginated */,
      );
      if (token !== fetchToken) return;
      creditMemos.value = page.data;

      const attachmentIds = new Set(currentAttachmentIds());
      const seeded = { ...creditMemoAttachmentIds.value };
      for (const cm of page.data) {
        if (seeded[cm.id]) continue;
        if (cm.pdf_id && attachmentIds.has(cm.pdf_id)) {
          seeded[cm.id] = cm.pdf_id;
        }
      }
      creditMemoAttachmentIds.value = seeded;
    } catch (err) {
      if (token !== fetchToken) return;
      console.error(err);
      toast.error('Failed to load credit memos.');
      creditMemos.value = [];
    }
  };

  const detachCreditMemo = async (cm: CreditMemo) => {
    const entity = toValue(options.entity);
    const attachment = attachmentForCreditMemo(cm);
    if (!entity || !attachment) return;

    fetchingCreditMemoIds.value.push(cm.id);
    try {
      await options.resource.removeAttachment(entity, attachment);
      delete creditMemoAttachmentIds.value[cm.id];
    } catch (err) {
      console.error(err);
      toast.error('Failed to detach credit memo.');
    } finally {
      fetchingCreditMemoIds.value = fetchingCreditMemoIds.value.filter((id) => id !== cm.id);
    }
  };

  const attachCreditMemo = async (cm: CreditMemo): Promise<boolean> => {
    const entity = toValue(options.entity);
    if (!entity) return false;

    if (!hasCreditMemoPdf(cm)) {
      toast.error("We don't have a file for this credit memo.");
      return false;
    }

    if (entity.attachmentsTotalSize >= MAX_TOTAL_ATTACHMENT_SIZE) {
      toast.error(
        `Attachments can't exceed ${formatFileSize(MAX_TOTAL_ATTACHMENT_SIZE)} in total.`,
      );
      return false;
    }

    fetchingCreditMemoIds.value.push(cm.id);
    try {
      const existingAttachmentIds = new Set(currentAttachmentIds());
      await options.resource.attachCreditMemo(entity, cm.id);
      const match =
        entity.attachments?.find((a) => !existingAttachmentIds.has(a.id)) ??
        entity.attachments?.find((a) => cm.pdf_id && a.id === cm.pdf_id);
      if (match) {
        creditMemoAttachmentIds.value = {
          ...creditMemoAttachmentIds.value,
          [cm.id]: match.id,
        };
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error('Failed to attach credit memo.');
      return false;
    } finally {
      fetchingCreditMemoIds.value = fetchingCreditMemoIds.value.filter((id) => id !== cm.id);
    }
  };

  const toggleAttachCreditMemo = async (cm: CreditMemo): Promise<boolean> => {
    if (toValue(options.disabled)) return false;
    if (isCreditMemoPending(cm)) return false;
    if (isCreditMemoAttached(cm)) {
      await detachCreditMemo(cm);
      return true;
    }
    return attachCreditMemo(cm);
  };

  const attachingAllCreditMemos = ref(false);

  const attachableCreditMemos = computed<CreditMemo[]>(() =>
    creditMemos.value.filter((cm) => hasCreditMemoPdf(cm) && !isCreditMemoAttached(cm)),
  );

  const allCreditMemosAttached = computed<boolean>(() => {
    const attachable = creditMemos.value.filter(hasCreditMemoPdf);
    if (!attachable.length) return false;
    return attachable.every((cm) => isCreditMemoAttached(cm));
  });

  const handleAttachAllCreditMemos = async () => {
    if (attachingAllCreditMemos.value || toValue(options.disabled)) return;
    attachingAllCreditMemos.value = true;

    try {
      const attachable = creditMemos.value.filter(hasCreditMemoPdf);
      const allAttached = attachable.every((cm) => isCreditMemoAttached(cm));
      const targets = allAttached
        ? attachable.filter((cm) => isCreditMemoAttached(cm))
        : attachable.filter((cm) => !isCreditMemoAttached(cm));

      for (const cm of targets) {
        await toggleAttachCreditMemo(cm);
      }
    } finally {
      attachingAllCreditMemos.value = false;
    }
  };

  watch(
    () => toValue(options.partnerId),
    (partnerId) => {
      if (partnerId) void fetchCreditMemos();
    },
    { immediate: true },
  );

  return {
    creditMemos,
    attachableCreditMemos,
    creditMemoSearch,
    toggleAttachCreditMemo,
    isCreditMemoAttached,
    isCreditMemoPending,
    allCreditMemosAttached,
    handleAttachAllCreditMemos,
  };
}
